import { User, UserManager, UserManagerSettings, WebStorageStateStore } from "oidc-client-ts";

import { HR_PERSONNEL_BASE_STATUS_STORAGE_KEY } from "Constants/common";
import { store } from "Redux/store";
import { setUserProfile } from "Redux/user/userSlice";

import { sendTokenToInvalidTokensList, serviceWorkerPostMessage } from "./Functions";

export interface IAuthService {
  /**
   * Amaldagi tokenni qaytaradi; muddati tugagan/tugayotgan bo'lsa avval refresh token bilan yangilaydi.
   * Yangilab bo'lmasa `null` qaytaradi — chaqiruvchi login'ga yo'naltirishi kerak.
   */
  ensureValidToken(force?: boolean): Promise<null | User>;

  getUser(): Promise<null | User>;

  login(): Promise<void>;

  logout(): Promise<void>;

  /** Sessiyani tiklab bo'lmaganda: joriy sahifani eslab qolib login'ga yuboradi. */
  redirectToLogin(): void;

  renewToken(): void;

  signinCallback(): Promise<User>;

  signinSilentCallback(): Promise<User | void>;

  signinSilentCallback(): Promise<undefined | User | void>;

  startSilentRenew(): void;
}

/** Login'dan keyin qaytib boriladigan sahifa shu kalitda saqlanadi (`SigninCallback` o'qiydi). */
export const RETURN_PATH_KEY = "current_path";
/** Cheksiz "login -> 401 -> login" aylanishidan himoya. */
const LOGIN_REDIRECT_GUARD_KEY = "auth_login_redirect_at";
const LOGIN_REDIRECT_GUARD_MS = 20000;
/** Muddati shu soniyadan kam qolgan token "eskirgan" hisoblanadi. */
const TOKEN_MIN_LIFETIME_SECONDS = 60;
/** Muddat tugashidan shuncha soniya OLDIN oldindan yangilaymiz. */
const TOKEN_RENEW_AHEAD_SECONDS = 120;

/**
 * MUHIM: `new AuthService()` ilova bo'ylab o'nlab joyda, hatto har render'da chaqiriladi.
 * Har biri alohida `UserManager` yaratsa — har birining o'z taymerlari va hodisa ishlovchilari
 * bo'lardi, natijada bir vaqtning o'zida bir nechta `signinSilent()` ketardi. IdentityServer'da
 * `RefreshTokenUsage = OneTimeOnly` (refresh token bir martalik), shuning uchun parallel
 * yangilashlarning biri albatta yiqilib sessiyani buzadi. Shu sababli `UserManager` ham,
 * yangilash promise'i ham butun ilova uchun BITTA.
 */
let userManagerInstance: null | UserManager = null;
let renewPromise: null | Promise<null | User> = null;
let loginRedirectInProgress = false;

/** Yangilangan tokenni Redux'ga va service worker'ga tarqatadi. */
const applyUser = (user: User) => {
  store.dispatch(setUserProfile(user));
  if (store.getState().settings.config?.notificationWithServiceWorker) {
    serviceWorkerPostMessage({ payload: user?.access_token, type: "SetToken", userId: user.profile.sub });
  }
};

export const saveReturnPath = () => {
  const path = window.location.pathname + window.location.search;
  // `/callback` va `/silent-renew` — texnik sahifalar, ularga qaytarish mantiqsiz.
  if (path && !path.startsWith("/callback") && !path.startsWith("/silent-renew")) {
    localStorage.setItem(RETURN_PATH_KEY, path);
  }
};

/**
 * Refresh token orqali jimgina yangilash. Bir vaqtda nechta chaqiruv bo'lsa ham
 * tarmoqqa bitta so'rov ketadi (single-flight).
 */
const renewSilently = (): Promise<null | User> => {
  if (!renewPromise) {
    renewPromise = getUserManager()
      .signinSilent()
      .then((user) => {
        if (user) {
          // `addUserLoaded` allaqachon Redux'ni yangilagan; tartib kafolatli bo'lsin uchun takrorlaymiz.
          applyUser(user);
          return user;
        }
        return null;
      })
      .catch((e) => {
        console.error("Renew Token Failed.", e);
        return null;
      })
      .finally(() => {
        renewPromise = null;
      });
  }
  return renewPromise;
};

const redirectToLoginInternal = () => {
  if (loginRedirectInProgress) {
    return;
  }
  const now = Date.now();
  const lastRedirect = Number(sessionStorage.getItem(LOGIN_REDIRECT_GUARD_KEY) || 0);
  if (lastRedirect && now - lastRedirect < LOGIN_REDIRECT_GUARD_MS) {
    // Hozirgina login qilib kelib yana 401 — sabab token emas (masalan huquq yoki tenant).
    // Aylanib qolmaslik uchun to'xtaymiz; xatoni sahifaning o'zi ko'rsatadi.
    console.error("401 after a fresh login - not redirecting again to avoid a loop.");
    return;
  }
  loginRedirectInProgress = true;
  sessionStorage.setItem(LOGIN_REDIRECT_GUARD_KEY, String(now));
  saveReturnPath();
  const userManager = getUserManager();
  // Eskirgan userni olib tashlaymiz, aks holda `getUser()` yana o'sha o'lik tokenni qaytaradi.
  void userManager
    .removeUser()
    .catch((e) => console.error("removeUser failed", e))
    .finally(() => {
      userManager.signinRedirect().catch((e) => {
        console.error("signinRedirect failed", e);
        loginRedirectInProgress = false;
      });
    });
};

const buildUserManager = (): UserManager => {
  const config = JSON.parse(localStorage.getItem("config") || JSON.stringify(""));
  const settings: UserManagerSettings = {
    accessTokenExpiringNotificationTimeInSeconds: TOKEN_RENEW_AHEAD_SECONDS,
    authority: config.identityUrl + "/",
    // Kutubxonaning o'z avto-yangilashi O'CHIQ: yangilashni bitta nuqtadan (`renewSilently`)
    // boshqaramiz, aks holda bir martalik refresh token uchun poyga chiqadi.
    automaticSilentRenew: false,
    client_id: "GermesWeb",
    loadUserInfo: true,
    post_logout_redirect_uri: config.appUrl ? config.appUrl : window.location.origin,
    redirect_uri: `${config.appUrl ? config.appUrl : window.location.origin}/callback`,
    response_type: "code",
    scope: "openid profile email offline_access phone address " + config.identityScopes,
    silent_redirect_uri: `${config.appUrl ? config.appUrl : window.location.origin}/silent-renew`,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  };

  const userManager = new UserManager(settings);

  // Token qaysi yo'l bilan yangilanmasin (silent renew, callback, refresh token) — yangisi shu
  // yerdan Redux'ga tushadi, `AuthProvider` esa uni Agent'ga qayta `RegisterApp` qiladi.
  // Agent tokenning O'Z NUSXASINI saqlagani uchun bu majburiy.
  userManager.events.addUserLoaded((user) => applyUser(user));
  userManager.events.addUserUnloaded(() => console.log("User unloaded"));
  // Muddat tugashidan oldin — oldindan yangilaymiz, foydalanuvchi hech narsa sezmaydi.
  userManager.events.addAccessTokenExpiring(() => {
    void renewSilently();
  });
  // Baribir tugab qolsa — oxirgi urinish. Bu yerda ATAYLAB login'ga yo'naltirmaymiz:
  // vaqtinchalik tarmoq uzilishi tufayli bekordan-bekorga sessiyadan chiqarib yubormaslik kerak.
  // Sessiya haqiqatan o'lgan bo'lsa, birinchi so'rovdayoq 401 keladi va o'sha yerda login'ga o'tamiz.
  userManager.events.addAccessTokenExpired(() => {
    void renewSilently().then((user) => {
      if (!user) {
        console.error("Access token expired and could not be renewed; waiting for the next request to redirect.");
      }
    });
  });
  userManager.events.addUserSignedOut(() => console.log("User signed out"));

  // Muddat taymerlari faqat `load()` chaqirilganda qo'yiladi — `getUser()` aynan shuni qiladi.
  void userManager.getUser().catch((e) => console.error("getUser failed", e));

  return userManager;
};

const getUserManager = (): UserManager => {
  if (!userManagerInstance) {
    userManagerInstance = buildUserManager();
  }
  return userManagerInstance;
};

class AuthService implements IAuthService {
  public userManager: UserManager;

  constructor() {
    this.userManager = getUserManager();
  }

  public async ensureValidToken(force = false): Promise<null | User> {
    const user = await this.userManager.getUser();
    if (!user) {
      // Sessiya umuman yo'q — yangilashga urinish ma'nosiz.
      return null;
    }
    if (!force && user.access_token && (user.expires_in ?? 0) > TOKEN_MIN_LIFETIME_SECONDS) {
      return user;
    }
    if (!user.refresh_token) {
      // Refresh token yo'q — `signinSilent()` yashirin iframe orqali urinib ko'radi va
      // IDP sessiyasi qisqa bo'lgani uchun ~10 soniya kutib baribir yiqiladi. Darrov taslim bo'lamiz.
      return null;
    }
    return renewSilently();
  }

  public getUser(): Promise<null | User> {
    return this.userManager.getUser();
  }

  public login(): Promise<void> {
    saveReturnPath();
    return this.userManager.signinRedirect();
  }

  public logout(): Promise<void> {
    sessionStorage.removeItem("agentNotEnabledOrDisconnected");
    sessionStorage.removeItem(LOGIN_REDIRECT_GUARD_KEY);
    // Ataylab chiqishda qaytib boriladigan sahifa saqlanmaydi.
    localStorage.removeItem(RETURN_PATH_KEY);
    // "База кадров" status filtri faqat shu sessiya uchun — logout'da tozalanadi
    localStorage.removeItem(HR_PERSONNEL_BASE_STATUS_STORAGE_KEY);
    if (store.getState().settings.config.agentConnectToApiHub) {
      return sendTokenToInvalidTokensList()
        .then(() => {})
        .catch((err) => console.log(`${err} AddInvalidToken`))
        .finally(() => this.userManager.signoutRedirect());
    } else {
      return this.userManager.signoutRedirect();
    }
  }

  public redirectToLogin(): void {
    redirectToLoginInternal();
  }

  public renewToken(): void {
    void renewSilently().then((user) => {
      if (!user) {
        redirectToLoginInternal();
      }
    });
  }

  public signinCallback(): Promise<User> {
    return this.userManager.signinRedirectCallback();
  }

  public signinSilentCallback(): Promise<undefined | User | void> {
    return this.userManager.signinSilentCallback().catch((error) => {
      console.error(error);
    });
  }

  public startSilentRenew(): void {
    this.userManager.startSilentRenew();
  }
}

export default AuthService;
